import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

/** Sections on the home page that the navbar and footer link to. */
export const HOME_SECTIONS = [
  { id: 'features', label: 'Features' },
  { id: 'ai', label: 'AI Recruitment' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
] as const;

type SectionState = { scrollTo?: string } | null;

export const scrollToSection = (
  id: string,
  behavior: ScrollBehavior = 'smooth',
) => document.getElementById(id)?.scrollIntoView({ behavior });

/**
 * Moves to a home page section without putting `#id` in the URL: scrolls
 * in place on the home page, otherwise opens it with the target in
 * router state (picked up by `useSectionScrollOnArrival`).
 */
const useSectionNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (id: string) => {
      if (pathname === '/') scrollToSection(id);
      else navigate('/', { state: { scrollTo: id } });
    },
    [navigate, pathname],
  );
};

/** Used by the home page to land on the section requested from another page. */
export const useSectionScrollOnArrival = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Old `/#pricing` style links still work; the hash is dropped afterwards.
    const target =
      (location.state as SectionState)?.scrollTo ||
      location.hash.replace('#', '');

    if (!target) return;

    const frame = requestAnimationFrame(() => scrollToSection(target, 'auto'));
    // Deferred: on a fresh page load the router ignores navigation this early.
    const timer = setTimeout(() =>
      navigate(location.pathname, { replace: true, state: null }),
    );
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
    // Only on arrival — clearing the state must not scroll again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useSectionNav;
